import React, { useState, useEffect, useRef, useMemo } from 'react';
import { reportHandledError } from '../utils/sentry';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  Plus, Trash2, Save, Search, ChevronDown, FlaskConical, Package,
  ArrowLeft, RefreshCcw, Database, ClipboardList, Check, Info,
  DollarSign, Calculator, ChevronRight, AlertTriangle, Download, Upload
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '../utils/numberFormat';
import { normalizeProductStatus } from '../utils/productStatus';

/**
 * Gestión de Recetas - Estándar Impulza Enterprise
 * Refactorizado: Actualización automática de costos basada en insumos actuales.
 */
const Recetas = ({ onNavigate }) => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // NUEVO ESTADO: Para la sincronización masiva
  const [syncingAll, setSyncingAll] = useState(false);

  // FIX UX: flag de organización para mostrar/ocultar el campo de merma esperada.
  // Se controla desde EmpresaDatos → "Merma Esperada en Recetas".
  const [enableMerma, setEnableMerma] = useState(false);

  // Maestros
  const [productosFinales, setProductosFinales] = useState([]);
  const [insumos, setInsumos] = useState([]); // Lista con costos actualizados de la DB
  
  // UI Controls
  const [showProdList, setShowProdList] = useState(false);
  const [showInsumoList, setShowInsumoList] = useState(false);
  const [busquedaProd, setBusquedaProd] = useState('');
  const [busquedaInsumo, setBusquedaInsumo] = useState('');

  // Inputs temporales
  const [tempInsumo, setTempInsumo] = useState(null);
  const [tempCantidad, setTempCantidad] = useState('');

  // Estado de la Receta
  // FIX UX #27: agregamos `merma_pct` (porcentaje de pérdida esperada).
  // Útil para calcular el costo unitario REAL: dividimos por (rendimiento * (1 - merma_pct/100))
  // en lugar de dividir solo por rendimiento. Defaults 0 → comportamiento legacy.
  const [receta, setReceta] = useState({
    product_id: '',
    producto_nombre: '',
    rendimiento: 1,
    merma_pct: 0,
    ingredientes: []
  });

  // Costo actual en base de datos del producto seleccionado (para comparar)
  const [costoActualDB, setCostoActualDB] = useState(0);

  const dropdownProdRef = useRef(null);
  const dropdownInsumoRef = useRef(null);

  useEffect(() => {
    if (profile?.organization_id) fetchData();
    const handleClickOutside = (e) => {
      if (dropdownProdRef.current && !dropdownProdRef.current.contains(e.target)) setShowProdList(false);
      if (dropdownInsumoRef.current && !dropdownInsumoRef.current.contains(e.target)) setShowInsumoList(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profile]);

  // Cuando cambia el producto seleccionado o se terminan de cargar los insumos, buscamos la receta
  useEffect(() => {
    if (receta.product_id && insumos.length > 0) {
        fetchExistingRecipe(receta.product_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receta.product_id, insumos.length]); 
  // Dependencia insumos.length es clave: asegura que recalculamos apenas tenemos los costos frescos

  const fetchData = async () => {
    try {
      setLoading(true);

      // FIX UX: leemos el flag enable_recipe_merma de la organización
      // en paralelo con los productos. Si la columna no existe (migración pendiente),
      // queda en false (campo oculto) sin romper la carga.
      const [prodsRes, orgRes] = await Promise.all([
        supabase
          // FIX: NO filtramos por is_active. Un insumo archivado que sigue siendo
          // ingrediente de una receta tiene que seguir visible (con aviso), o el
          // costo de la receta queda congelado en silencio.
          .from('products')
          .select('id, name, cost_price, product_type, active, is_active, units_of_measure(abbreviation)')
          .eq('organization_id', profile.organization_id)
          .order('name', { ascending: true }),
        supabase
          .from('organizations')
          .select('enable_recipe_merma')
          .eq('id', profile.organization_id)
          .maybeSingle(),
      ]);

      if (prodsRes.error) throw prodsRes.error;
      // Si orgRes falla por columna inexistente, simplemente queda en false
      if (orgRes.error && orgRes.error.code !== 'PGRST204') {
        console.warn('[Recetas.fetchData] No se pudo leer enable_recipe_merma:', orgRes.error);
      }

      // Resolvemos el estado activo con el helper compartido: la tabla tiene
      // `active` e `is_active` y no siempre viajan juntas.
      const allProds = (prodsRes.data || []).map((p) => {
        const status = normalizeProductStatus(p);
        return { ...p, archivado: status.active === false };
      });

      // Separamos en Finales (Para fabricar) e Insumos (Para usar)
      // Nota: Un 'Producto Terminado' también podría ser insumo de otro (producto intermedio),
      // así que permitimos que insumos sean casi todo.
      // Los archivados quedan en ambas listas: si desaparecen, la receta que los usa
      // se vuelve ineditable y su costo no se puede corregir nunca más.
      setProductosFinales(allProds.filter(p => p.product_type?.toLowerCase().includes('terminado')));
      setInsumos(allProds); // Permitimos cualquier producto como insumo (flexibilidad)
      setEnableMerma(Boolean(orgRes.data?.enable_recipe_merma));

    } catch (error) {
      console.error(error);
      toast.error("Error de sincronización maestros");
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingRecipe = async (pid) => {
    // 1. Buscamos la receta guardada (filtrando por organización para defensa en profundidad)
    const { data } = await supabase
      .from('recetas')
      .select('*')
      .eq('producto_id', pid)
      .eq('organization_id', profile.organization_id)
      .maybeSingle();

    // 2. FIX IMPORTANTE #15: buscamos el costo actual en TODOS los productos cargados
    // (insumos), no solo en productosFinales. Antes, si el producto seleccionado era
    // un intermedio que no matcheaba product_type='terminado', costoActualDB quedaba
    // en 0 y el KPI marcaba "Desfasado" siempre. Usamos la lista completa `insumos`
    // que ya contiene todos los productos activos de la org.
    const prod = insumos.find(p => p.id === pid);
    setCostoActualDB(prod ? Number(prod.cost_price || 0) : 0);

    if (data) {
      // --- LÓGICA DE ACTUALIZACIÓN AUTOMÁTICA DE COSTOS (HIDRATACIÓN) ---
      // Recorremos los ingredientes guardados y actualizamos su costo unitario 
      // con el valor ACTUAL de la tabla de productos (insumos).
      const ingredientesActualizados = (data.ingredientes || []).map(ing => {
          const insumoFresco = insumos.find(i => i.id === ing.id);
          // Si encontramos el insumo, usamos su precio nuevo. Si no (se borró?), mantenemos el viejo.
          const costoReal = insumoFresco ? Number(insumoFresco.cost_price || 0) : Number(ing.costo_unitario || 0);
          
          return {
              ...ing,
              nombre: insumoFresco ? insumoFresco.name : ing.nombre, // Actualizamos nombre también por si cambió
              costo_unitario: costoReal
          };
      });

      setReceta(prev => ({
          ...prev,
          ingredientes: ingredientesActualizados,
          rendimiento: Number(data.rendimiento) || 1,
          // FIX UX #27: cargar merma persistida o 0 por defecto (back-compat con recetas legacy)
          merma_pct: Math.max(0, Math.min(99, Number(data.merma_pct || 0))),
      }));

      // Si hubo cambios de precio, el usuario lo notará en el KPI de "Costo Unitario" vs lo que tenía antes.
    } else {
      setReceta(prev => ({ ...prev, ingredientes: [], rendimiento: 1, merma_pct: 0 }));
    }
  };

  const resetForm = () => {
    setReceta({ product_id: '', producto_nombre: '', rendimiento: 1, merma_pct: 0, ingredientes: [] });
    setBusquedaProd('');
    setBusquedaInsumo('');
    setTempInsumo(null);
    setTempCantidad('');
    setIsSuccess(false);
    setCostoActualDB(0);
  };

  const handleAddIngrediente = () => {
    const cantNum = parseFloat(tempCantidad);
    if (!tempInsumo || !Number.isFinite(cantNum) || cantNum <= 0) {
      return toast.error("Seleccione insumo y cantidad válida (> 0)");
    }

    // FIX CRÍTICO: bloquear que un producto se referencie a sí mismo en su propia receta
    // (rompe el costeo recursivo y la sincronización masiva).
    if (receta.product_id && tempInsumo.id === receta.product_id) {
      return toast.error("Un producto no puede ser ingrediente de su propia receta");
    }

    // Los archivados se conservan en las recetas viejas, pero no se agregan de nuevo.
    if (tempInsumo.archivado) {
      return toast.error("Ese insumo está archivado. Reactivalo en Productos para usarlo.");
    }

    // FIX IMPORTANTE: si el insumo no tiene unidad de medida configurada, advertir.
    // Mezclar gr/kg/un sin conversión es una fuente típica de errores de costo.
    const um = tempInsumo.units_of_measure?.abbreviation || null;
    if (!um) {
      toast("Atención: el insumo no tiene unidad de medida configurada", { icon: '⚠️' });
    }

    // Validar duplicados → sumar cantidad si ya existe
    const existe = receta.ingredientes.find(i => i.id === tempInsumo.id);
    if (existe) {
        const nuevos = receta.ingredientes.map(i =>
          i.id === tempInsumo.id
            ? { ...i, quantity: Number(i.quantity) + cantNum }
            : i
        );
        setReceta({ ...receta, ingredientes: nuevos });
        setTempInsumo(null); setTempCantidad(''); setBusquedaInsumo('');
        return;
    }

    const nuevo = {
      id: tempInsumo.id,
      nombre: tempInsumo.name,
      quantity: cantNum,
      um: um || 'un',
      costo_unitario: Number(tempInsumo.cost_price || 0) // Usamos el costo FRESH del estado
    };
    setReceta({ ...receta, ingredientes: [...receta.ingredientes, nuevo] });
    setTempInsumo(null); setTempCantidad(''); setBusquedaInsumo('');
  };

  const removeIngrediente = (idx) => {
    setReceta({ ...receta, ingredientes: receta.ingredientes.filter((_, i) => i !== idx) });
  };

  const updateQuantity = (idx, val) => {
    // FIX CRÍTICO #10: respetar inmutabilidad. Antes se mutaba newIng[idx].quantity en el array clonado superficialmente,
    // lo cual mantenía la misma referencia del objeto y rompía useMemo de costoTotalReceta en escenarios de batching de React.
    const parsed = parseFloat(val);
    const safeQty = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    setReceta((prev) => ({
      ...prev,
      ingredientes: prev.ingredientes.map((ing, i) =>
        i === idx ? { ...ing, quantity: safeQty } : ing,
      ),
    }));
  };

  // --- CÁLCULOS DINÁMICOS ---
  // 1. Costo Total de la Mezcla = Suma (Cantidad Insumo * Costo Unitario Insumo)
  const costoTotalReceta = useMemo(() => {
    return receta.ingredientes.reduce((acc, curr) => {
        return acc + (Number(curr.costo_unitario || 0) * Number(curr.quantity || 0));
    }, 0);
  }, [receta.ingredientes]);

  // 2. Costo Unitario Final = Costo Total Mezcla / (Rendimiento * (1 - merma/100))
  // FIX UX #27: si la receta declara merma Y la organización tiene el toggle activo,
  // el rendimiento "real" es menor que el teórico, por lo que el costo unitario REAL
  // del producto bueno es mayor. Si el toggle está apagado, la merma se ignora aunque
  // la receta la tenga persistida (back-compat con cálculo legacy).
  const costoUnitarioFinal = useMemo(() => {
     const rend = Number(receta.rendimiento) || 1;
     const merma = enableMerma
       ? Math.max(0, Math.min(99, Number(receta.merma_pct || 0)))
       : 0;
     const rendReal = rend * (1 - merma / 100);
     return rendReal > 0 ? costoTotalReceta / rendReal : 0;
  }, [costoTotalReceta, receta.rendimiento, receta.merma_pct, enableMerma]);

  // 3. Detectar si hay desfasaje con la DB (Para alertar visualmente)
  const hayDesfasaje = Math.abs(costoUnitarioFinal - costoActualDB) > 0.01 && receta.product_id;

  // Ids de productos archivados. El flag NO se guarda dentro del ingrediente:
  // se deriva del maestro en cada render para no ensuciar el JSON de la receta.
  const archivedIds = useMemo(
    () => new Set(insumos.filter((p) => p.archivado).map((p) => p.id)),
    [insumos],
  );

  // Insumos ofrecidos para AGREGAR: solo activos. Los archivados se conservan en
  // las recetas existentes, pero no se pueden sumar como ingrediente nuevo.
  const insumosSeleccionables = useMemo(
    () => insumos.filter((i) => !i.archivado),
    [insumos],
  );

  const ingredientesArchivados = useMemo(
    () => (receta.ingredientes || []).filter((ing) => archivedIds.has(ing.id)),
    [receta.ingredientes, archivedIds],
  );

  const guardarReceta = async () => {
    if (!receta.product_id || receta.ingredientes.length === 0) return toast.error("Complete la información básica");
    if (!profile?.organization_id) return toast.error("Sesión no válida");

    // FIX CRÍTICO #6: bloquear pisado de cost_price con 0.
    // Si todos los ingredientes tienen quantity 0 o costo 0, el costoUnitarioFinal sería 0
    // y destruiríamos el costo histórico del producto, falseando todos los márgenes.
    const ingsValidos = receta.ingredientes.filter(
      (ing) => Number(ing.quantity || 0) > 0 && Number(ing.costo_unitario || 0) > 0,
    );
    if (ingsValidos.length === 0 || costoUnitarioFinal <= 0) {
      return toast.error(
        "El costo calculado es 0. Verifique cantidades y costos de insumos antes de guardar.",
      );
    }

    setSaving(true);
    try {
      // 1. Guardar/Actualizar la definición de la Receta
      // FIX: onConflict ahora incluye organization_id para evitar colisiones cross-org
      // (requiere índice único compuesto en la tabla — ver migración SQL).
      const basePayload = {
        organization_id: profile.organization_id,
        producto_id: receta.product_id,
        ingredientes: receta.ingredientes,
        rendimiento: receta.rendimiento,
      };
      // FIX UX #27: persistir la merma esperada (default 0 = sin merma)
      const mermaSafe = Math.max(0, Math.min(99, Number(receta.merma_pct || 0)));

      let { error: errorReceta } = await supabase
        .from('recetas')
        .upsert({ ...basePayload, merma_pct: mermaSafe }, { onConflict: 'organization_id,producto_id' });

      // Fallback defensivo: si la columna `merma_pct` aún no fue agregada
      // (migración 20260515b_recetas_merma_pct.sql no aplicada), reintentamos
      // sin ese campo y avisamos al usuario una vez.
      if (errorReceta && errorReceta.code === 'PGRST204' && /merma_pct/i.test(errorReceta.message || '')) {
        console.warn('[Recetas] Columna merma_pct no existe en la DB; aplicá la migración 20260515b. Guardando sin merma.');
        toast('Tip: aplicá la migración SQL para habilitar el campo de merma.', { icon: '⚠️', duration: 6000 });
        const retry = await supabase
          .from('recetas')
          .upsert(basePayload, { onConflict: 'organization_id,producto_id' });
        errorReceta = retry.error;
      }

      if (errorReceta) throw errorReceta;

      // 2. ACTUALIZAR COSTO MAESTRO DEL PRODUCTO FINAL
      // Filtramos también por organization_id como defensa en profundidad (RLS + filtro explícito).
      const { error: errorProd } = await supabase
        .from('products')
        .update({ cost_price: costoUnitarioFinal })
        .eq('id', receta.product_id)
        .eq('organization_id', profile.organization_id);

      if (errorProd) throw errorProd;

      // Sincronizar variantes de este producto si existen (Mismo precio de costo)
      await supabase
        .from('product_variants')
        .update({ cost_price: costoUnitarioFinal })
        .eq('product_id', receta.product_id);

      // Actualizamos estado local para reflejar que ya están sincronizados
      setCostoActualDB(costoUnitarioFinal);
      setIsSuccess(true);
      toast.success("Receta guardada y Costo Maestro actualizado");

      setTimeout(() => {
        setIsSuccess(false);
        setSaving(false);
      }, 2000);

    } catch (err) {
      console.error(err);
      toast.error("Error al procesar la operación");
      setSaving(false);
    }
  };

  /**
   * Devuelve las recetas ordenadas topológicamente: primero las que NO dependen
   * de otras recetas (insumos puros), después las intermedias, al final las finales.
   * Si hay ciclo, las que queden sin resolver se procesan al final con warning.
   *
   * FIX CRÍTICO #4: sin este orden, una receta B que usa A como insumo se recalcula
   * con el cost_price viejo de A, propagando costos desactualizados en cadena.
   */
  const ordenarRecetasTopologicamente = (allRecetas) => {
    const recetaPorProducto = new Map(allRecetas.map((r) => [r.producto_id, r]));
    const visitadas = new Set();
    const enCurso = new Set();
    const ordenadas = [];
    const ciclos = [];

    const visitar = (productoId, path = []) => {
      if (visitadas.has(productoId)) return;
      if (enCurso.has(productoId)) {
        ciclos.push([...path, productoId]);
        return;
      }
      enCurso.add(productoId);
      const rec = recetaPorProducto.get(productoId);
      if (rec) {
        (rec.ingredientes || []).forEach((ing) => {
          if (ing.id && recetaPorProducto.has(ing.id) && ing.id !== productoId) {
            visitar(ing.id, [...path, productoId]);
          }
        });
        ordenadas.push(rec);
      }
      enCurso.delete(productoId);
      visitadas.add(productoId);
    };

    allRecetas.forEach((r) => visitar(r.producto_id));
    return { ordenadas, ciclos };
  };

  // --- SINCRONIZACIÓN MASIVA (BOTÓN MÁGICO) ---
  const handleSyncAll = async () => {
    if (!profile?.organization_id) return;

    // FIX MEJORA #23: confirmación previa antes de tocar costos/precios de TODA la org
    const confirmar = window.confirm(
      '¿Confirmás recalcular costos y precios de TODOS los productos con receta de la organización?\n\n' +
      'Esta acción procesa las recetas en orden de dependencia (productos intermedios primero) ' +
      'y solo actualiza precios de venta cuando el producto tiene "Auto-actualizar precio" y un margen objetivo > 0.',
    );
    if (!confirmar) return;

    setSyncingAll(true);
    const toastId = toast.loading('Sincronizando costos globales...');

    try {
        // 1. Traer todas las recetas de la organización
        const { data: allRecetas, error: recErr } = await supabase
            .from('recetas')
            .select('*')
            .eq('organization_id', profile.organization_id);
        if (recErr) throw recErr;

        // 2. Traer todos los productos para ver precios actuales y márgenes
        const { data: allProducts, error: prodErr } = await supabase
            .from('products')
            .select('id, cost_price, auto_update_price, target_margin')
            .eq('organization_id', profile.organization_id);
        if (prodErr) throw prodErr;

        // Diccionario MUTABLE de costos: a medida que recalculamos productos intermedios,
        // actualizamos el costMap para que las recetas que dependen de ellos usen el costo nuevo.
        const costMap = new Map();
        allProducts.forEach((p) => { costMap.set(p.id, Number(p.cost_price || 0)); });

        // FIX CRÍTICO #4: orden topológico para procesar dependencias primero
        const { ordenadas, ciclos } = ordenarRecetasTopologicamente(allRecetas);
        if (ciclos.length > 0) {
          console.warn('[Recetas.handleSyncAll] Se detectaron ciclos de dependencia:', ciclos);
          toast(
            `Atención: se detectaron ${ciclos.length} ciclo(s) en las recetas. Revise la configuración.`,
            { icon: '⚠️', duration: 6000 },
          );
        }

        let updatedCount = 0;
        let skippedZeroCost = 0;
        let skippedNoMargin = 0;
        const productIdsActualizados = [];

        // 3. Iterar en orden topológico y recalcular
        for (const rec of ordenadas) {
            const prodFinal = allProducts.find((p) => p.id === rec.producto_id);
            if (!prodFinal) continue;

            let costoTotalMezcla = 0;
            let ingredienteSinCosto = false;
            (rec.ingredientes || []).forEach((ing) => {
                // Usamos el costo MUTABLE del costMap (ya actualizado si el insumo es intermedio)
                const costoFresco = costMap.has(ing.id)
                  ? Number(costMap.get(ing.id) || 0)
                  : Number(ing.costo_unitario || 0);
                if (costoFresco <= 0) ingredienteSinCosto = true;
                costoTotalMezcla += (costoFresco * Number(ing.quantity || 0));
            });

            const rend = Number(rec.rendimiento) || 1;
            const nuevoCostoUnitario = rend > 0 ? (costoTotalMezcla / rend) : 0;
            const nuevoCostoRedondeado = Number(nuevoCostoUnitario.toFixed(2));
            const costoActual = Number(prodFinal.cost_price || 0);

            // FIX CRÍTICO #6: NUNCA pisar el cost_price con 0 (destruye el costo histórico)
            if (nuevoCostoRedondeado <= 0) {
                skippedZeroCost++;
                continue;
            }

            // Si hay desfasaje, actualizamos en la Base de Datos
            if (Math.abs(nuevoCostoRedondeado - costoActual) > 0.01) {
                let nuevoPrecioVenta;

                // FIX CRÍTICO #5: validar margen > 0 antes de tocar el precio.
                // Sin esto, target_margin=0 fija precio=costo (margen 0%) y rompe ventas.
                if (prodFinal.auto_update_price) {
                    const margin = Number(prodFinal.target_margin || 0);
                    if (margin > 0) {
                        nuevoPrecioVenta = Number((nuevoCostoRedondeado * (1 + (margin / 100))).toFixed(2));
                    } else {
                        skippedNoMargin++;
                    }
                }

                const upPayload = { cost_price: nuevoCostoRedondeado };
                if (nuevoPrecioVenta !== undefined) upPayload.sell_price = nuevoPrecioVenta;

                // Actualizamos Producto (con filtro por org como defensa en profundidad)
                const { error: updProdErr } = await supabase
                  .from('products')
                  .update(upPayload)
                  .eq('id', rec.producto_id)
                  .eq('organization_id', profile.organization_id);
                if (updProdErr) {
                  console.error('[Recetas.handleSyncAll] Error actualizando producto', rec.producto_id, updProdErr);
                  continue; // seguimos con los demás
                }

                // IMPORTANTE: actualizamos el costMap para que recetas posteriores
                // (que ya están ordenadas topológicamente después) usen el nuevo valor.
                costMap.set(rec.producto_id, nuevoCostoRedondeado);

                // Variantes: solo actualizamos cost_price; el price queda en cada variante
                // porque puede tener márgenes diferenciales (FIX IMPORTANTE).
                await supabase
                  .from('product_variants')
                  .update({ cost_price: nuevoCostoRedondeado })
                  .eq('product_id', rec.producto_id);

                updatedCount++;
                productIdsActualizados.push(rec.producto_id);
            }
        }

        // Aviso: recetas que dependen de un insumo archivado. El costo se recalcula igual,
        // pero ese insumo ya no se actualiza por Compras ni por Gestión de Costos.
        const idsArchivados = new Set(insumos.filter((p) => p.archivado).map((p) => p.id));
        const recetasConArchivados = allRecetas.filter((rec) =>
          (rec.ingredientes || []).some((ing) => idsArchivados.has(ing.id)),
        ).length;

        const detalles = [];
        if (skippedZeroCost > 0) detalles.push(`${skippedZeroCost} sin costo válido`);
        if (skippedNoMargin > 0) detalles.push(`${skippedNoMargin} sin margen configurado`);
        const sufijo = detalles.length ? ` (omitidos: ${detalles.join(', ')})` : '';

        if (recetasConArchivados > 0) {
            toast(
              `${recetasConArchivados} receta(s) usan insumos archivados: revisá su costo.`,
              { icon: '⚠️', duration: 6000 },
            );
        }

        if (updatedCount === 0) {
            toast.success(`Todos los costos ya estaban al día.${sufijo}`, { id: toastId });
        } else {
            toast.success(`Se actualizaron ${updatedCount} producto(s).${sufijo}`, { id: toastId });
            if (receta.product_id) {
                fetchExistingRecipe(receta.product_id);
            } else {
                fetchData();
            }
        }

    } catch (error) {
        console.error("Error en sincronización global:", error);
        reportHandledError(error, { feature: 'produccion:costos-globales' });
        toast.error('Error al sincronizar costos globales', { id: toastId });
    } finally {
        setSyncingAll(false);
    }
  };

  // FIX IMPORTANTE #22: respeta locale/currency de la organización en lugar de es-AR/ARS hardcoded
  const fmtMoney = (n) => formatCurrency(n, { orgId: profile?.organization_id });

  // FIX UX #29: Exportar todas las recetas de la organización a CSV.
  // Formato: una fila por (receta, ingrediente). Permite re-importar después de editar.
  const handleExportCsv = async () => {
    if (!profile?.organization_id) return;
    const toastId = toast.loading('Exportando recetas...');
    try {
      const { data: recetas, error } = await supabase
        .from('recetas')
        .select('producto_id, rendimiento, merma_pct, ingredientes')
        .eq('organization_id', profile.organization_id);
      if (error) throw error;

      // Mapa de id → nombre y código
      const productMap = new Map(insumos.map((p) => [p.id, p]));

      const headers = [
        'producto_id',
        'producto_nombre',
        'rendimiento',
        'merma_pct',
        'ingrediente_id',
        'ingrediente_nombre',
        'cantidad',
        'unidad',
      ];
      const rows = [headers.join(',')];

      const csvEscape = (v) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      (recetas || []).forEach((rec) => {
        const prodFinal = productMap.get(rec.producto_id);
        (rec.ingredientes || []).forEach((ing) => {
          const insumo = productMap.get(ing.id);
          rows.push([
            rec.producto_id,
            csvEscape(prodFinal?.name || ''),
            rec.rendimiento ?? 1,
            rec.merma_pct ?? 0,
            ing.id || '',
            csvEscape(insumo?.name || ing.nombre || ''),
            ing.quantity ?? 0,
            csvEscape(ing.um || insumo?.units_of_measure?.abbreviation || ''),
          ].join(','));
        });
      });

      const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `impulza-recetas-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Exportadas ${recetas?.length || 0} receta(s)`, { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error('Error al exportar', { id: toastId });
    }
  };

  // FIX UX #29: Importar recetas desde CSV con el mismo formato que exportamos.
  // Estrategia: parseamos por producto_id, agrupamos ingredientes y hacemos upsert por receta.
  const handleImportCsv = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // permitir reimportar el mismo archivo
    if (!file) return;
    if (!profile?.organization_id) return;

    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) {
      toast.error('CSV vacío o sin encabezados');
      return;
    }

    // Parser CSV simple respetando comillas
    const parseLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"' && line[i + 1] === '"') { current += '"'; i++; }
        else if (ch === '"') { inQuotes = !inQuotes; }
        else if (ch === ',' && !inQuotes) { result.push(current); current = ''; }
        else { current += ch; }
      }
      result.push(current);
      return result;
    };

    const headers = parseLine(lines[0]).map((h) => h.trim());
    const rows = lines.slice(1).map(parseLine);

    // Agrupar por producto_id
    const recetasMap = new Map();
    rows.forEach((cols) => {
      const obj = {};
      headers.forEach((h, i) => { obj[h] = cols[i]; });
      const productoId = obj.producto_id;
      if (!productoId) return;
      const existing = recetasMap.get(productoId) || {
        producto_id: productoId,
        rendimiento: Number(obj.rendimiento) || 1,
        merma_pct: Math.max(0, Math.min(99, Number(obj.merma_pct) || 0)),
        ingredientes: [],
      };
      if (obj.ingrediente_id) {
        existing.ingredientes.push({
          id: obj.ingrediente_id,
          nombre: obj.ingrediente_nombre || '',
          quantity: Number(obj.cantidad) || 0,
          um: obj.unidad || 'un',
          costo_unitario: 0, // se hidrata desde products en el sync
        });
      }
      recetasMap.set(productoId, existing);
    });

    const recetasParseadas = Array.from(recetasMap.values());
    if (recetasParseadas.length === 0) {
      toast.error('No se encontraron recetas válidas en el CSV');
      return;
    }

    if (!window.confirm(
      `Vas a importar ${recetasParseadas.length} receta(s). Las existentes para los mismos productos ` +
      `serán SOBREESCRITAS. Después correr "Sincronizar Todo" para recalcular costos. ¿Continuar?`,
    )) return;

    const toastId = toast.loading('Importando recetas...');
    try {
      // Upsert en lote (con merma_pct si la columna existe)
      const buildPayload = (incluyeMerma) => recetasParseadas.map((r) => {
        const base = {
          organization_id: profile.organization_id,
          producto_id: r.producto_id,
          ingredientes: r.ingredientes,
          rendimiento: r.rendimiento,
        };
        return incluyeMerma ? { ...base, merma_pct: r.merma_pct } : base;
      });

      let { error } = await supabase
        .from('recetas')
        .upsert(buildPayload(true), { onConflict: 'organization_id,producto_id' });

      // Fallback defensivo: si la columna merma_pct no existe (migración pendiente),
      // reintentamos sin ella para no bloquear al usuario.
      if (error && error.code === 'PGRST204' && /merma_pct/i.test(error.message || '')) {
        console.warn('[Recetas.import] Columna merma_pct no existe; reintentando sin ella.');
        toast('Tip: aplicá la migración SQL para habilitar el campo de merma.', { icon: '⚠️', duration: 6000 });
        const retry = await supabase
          .from('recetas')
          .upsert(buildPayload(false), { onConflict: 'organization_id,producto_id' });
        error = retry.error;
      }

      if (error) throw error;
      toast.success(`Importadas ${recetasParseadas.length} receta(s). Ejecutá "Sincronizar Todo" para recalcular costos.`, { id: toastId, duration: 6000 });
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error('Error al importar: ' + (err?.message || ''), { id: toastId });
    }
  };

  return (
    <div className="w-full p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <FlaskConical size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Ingeniería de Productos</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Gestión de fórmulas y actualización de costos.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* FIX UX #29: import/export CSV */}
          <button
            type="button"
            onClick={handleExportCsv}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 hover:text-[#51A7FD] shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            title="Descargar todas las recetas en CSV"
          >
            <Download size={16} />
            <span className="hidden md:inline">Exportar</span>
          </button>
          <label
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 hover:text-[#51A7FD] shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 cursor-pointer"
            title="Importar recetas desde CSV (mismo formato que Exportar)"
          >
            <Upload size={16} />
            <span className="hidden md:inline">Importar</span>
            <input type="file" accept=".csv,text/csv" onChange={handleImportCsv} className="hidden" />
          </label>

          {/* BOTÓN MÁGICO AÑADIDO ACÁ */}
          <button
            type="button"
            onClick={handleSyncAll}
            disabled={syncingAll}
            className="px-4 py-2.5 rounded-2xl bg-amber-100 border border-amber-200 text-amber-700 hover:bg-amber-200 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
            title="Recalcula y actualiza automáticamente los costos de todos los productos que tengan receta."
          >
            {syncingAll ? <RefreshCcw size={16} className="animate-spin" /> : <RefreshCcw size={16} />}
            <span className="hidden md:inline">Sincronizar Todo</span>
          </button>

          <button 
            type="button"
            onClick={() => onNavigate && onNavigate('productos')} 
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 hover:text-[#51A7FD] shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <ArrowLeft size={16} /> Volver
          </button>
        </div>
      </div>

      {/* KPI SECTION */}
      <div data-onboarding-id="recetas-costo-resumen" className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {/* KPI 1: Costo Unitario Final (Calculado) */}
        <div className={`bg-white p-4 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group transition-all ${hayDesfasaje ? 'hover:border-amber-300 ring-2 ring-amber-200' : 'hover:border-emerald-300'}`}>
          <div className="flex items-center justify-between relative z-10">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Costo Unitario Final</p>
            <DollarSign size={16} className={hayDesfasaje ? "text-amber-500" : "text-emerald-500"} />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight relative z-10">
            {fmtMoney(costoUnitarioFinal)}
          </p>
          <div className="flex items-center gap-2 mt-1 relative z-10">
             <p className={`text-[10px] font-bold uppercase tracking-widest ${hayDesfasaje ? 'text-amber-600' : 'text-emerald-600'}`}>
               {hayDesfasaje ? 'Pendiente de actualizar' : 'Calculado'}
             </p>
             {hayDesfasaje && (
                 <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex items-center gap-1 font-bold">
                    <AlertTriangle size={10} /> Desfasado
                 </span>
             )}
          </div>
          <div className={`absolute -right-4 -bottom-4 opacity-10 group-hover:opacity-20 transition-opacity ${hayDesfasaje ? 'text-amber-300' : 'text-emerald-200'}`}>
            <DollarSign size={80} />
          </div>
        </div>

        {/* KPI 2: Costo Total Lote */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-slate-300 transition-all">
          <div className="flex items-center justify-between relative z-10">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Costo Total Lote</p>
            <Calculator size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight relative z-10">
             {fmtMoney(costoTotalReceta)}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Suma de insumos</p>
          <div className="absolute -right-4 -bottom-4 text-slate-100 opacity-50 group-hover:opacity-100 transition-opacity">
            <Calculator size={80} />
          </div>
        </div>

        {/* KPI 3: Cantidad Insumos */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-[#51A7FD]/30 transition-all">
            <div className="flex items-center justify-between relative z-10">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Insumos</p>
                <Package size={16} className="text-slate-300 group-hover:text-[#51A7FD] transition-colors" />
            </div>
            <p className={`mt-1.5 text-2xl font-black tracking-tight relative z-10 ${receta.ingredientes.length > 0 ? 'text-[#51A7FD]' : 'text-slate-300'}`}>
                {receta.ingredientes.length}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Componentes activos</p>
            <div className="absolute -right-4 -bottom-4 text-blue-50 opacity-10 group-hover:opacity-20 transition-opacity">
              <Package size={80} />
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        
        {/* 1. SELECCIÓN DE PRODUCTO Y RENDIMIENTO */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center gap-2">
            <Database size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
              Configuración Base
            </span>
          </div>
          <div className="p-5 grid grid-cols-1 gap-4">
             {/* BUSCADOR DE PRODUCTO FINAL */}
             <div className="relative z-20" ref={dropdownProdRef}>
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">
                    <Database size={10} /> 1. Selección de Producto a Fabricar
                </label>
                
                <div
                  data-onboarding-id="recetas-product-select"
                  onClick={() => setShowProdList(!showProdList)}
                  className="w-full pl-3 pr-3 py-3 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none shadow-inner uppercase text-slate-700 flex justify-between items-center cursor-pointer hover:border-[#51A7FD] transition-all"
                >
                  <span className={receta.producto_nombre ? "text-slate-800" : "text-slate-400"}>
                     {receta.producto_nombre || "SELECCIONAR PRODUCTO..."}
                  </span>
                  <ChevronDown size={16} className="text-slate-400" />
                </div>

                {showProdList && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl max-h-60 overflow-y-auto overflow-x-hidden animate-nova-in p-2">
                    <div className="relative mb-2">
                         <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                         <input 
                            type="text" 
                            placeholder="FILTRAR..." 
                            value={busquedaProd} 
                            autoFocus 
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setBusquedaProd(e.target.value)} 
                            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold uppercase outline-none" 
                         />
                    </div>
                    {/* Archivados fuera del selector: no se arman recetas nuevas sobre productos dados de baja.
                        Las recetas existentes siguen cargándose (productosFinales conserva todos). */}
                    {productosFinales.filter(p => !p.archivado && p.name.toLowerCase().includes(busquedaProd.toLowerCase())).map(p => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => { setReceta({...receta, product_id: p.id, producto_nombre: p.name}); setShowProdList(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 rounded-xl transition-colors flex items-center justify-between group"
                        >
                            <span className="text-[10px] font-black text-slate-700 uppercase flex items-center gap-1.5">
                              {p.name}
                            </span>
                            <ChevronRight size={14} className="text-slate-300 group-hover:text-[#51A7FD]" />
                        </button>
                    ))}
                  </div>
                )}
             </div>

             {/* RENDIMIENTO */}
             <div>
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">
                    Rendimiento
                </label>
                <input
                    type="number"
                    value={receta.rendimiento}
                    onChange={(e) => setReceta({...receta, rendimiento: parseFloat(e.target.value) || 1})}
                    aria-label="Rendimiento de la receta (unidades por lote)"
                    className="w-full px-3 py-3 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black text-[#51A7FD] outline-none shadow-inner focus:border-[#51A7FD] focus:ring-2 focus:ring-[#51A7FD]/10 text-center"
                />
                <p className="mt-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                  Unidades producidas por lote
                </p>
             </div>

             {/* FIX UX #27: MERMA ESPERADA — visible solo si la organización
                 activó el toggle "Merma Esperada en Recetas" en EmpresaDatos. */}
             {enableMerma && (
               <div>
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">
                      Merma esperada (%)
                  </label>
                  <input
                      type="number"
                      min="0"
                      max="99"
                      step="0.1"
                      value={receta.merma_pct}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        const clamped = Number.isFinite(val) ? Math.max(0, Math.min(99, val)) : 0;
                        setReceta({ ...receta, merma_pct: clamped });
                      }}
                      aria-label="Porcentaje de merma esperada"
                      className="w-full px-3 py-3 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black text-amber-600 outline-none shadow-inner focus:border-amber-500 focus:ring-2 focus:ring-amber-500/10 text-center"
                  />
                  <p className="mt-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                    {receta.merma_pct > 0
                      ? `Rendimiento real: ${(Number(receta.rendimiento || 0) * (1 - Number(receta.merma_pct || 0) / 100)).toFixed(2)} unidades`
                      : 'Sin merma esperada'}
                  </p>
               </div>
             )}
             <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${hayDesfasaje ? 'bg-amber-50 text-amber-500' : 'bg-emerald-50 text-emerald-500'}`}>
                    {hayDesfasaje ? <AlertTriangle size={18} /> : <Info size={18} />}
                  </div>
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Estado de sincronización</p>
                    <p className={`mt-1 text-sm font-black ${hayDesfasaje ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {hayDesfasaje ? 'El costo calculado no coincide con el costo maestro actual' : 'La receta y el costo maestro están alineados'}
                    </p>
                    <p className="mt-2 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                      Costo actual en ficha: {fmtMoney(costoActualDB)}
                    </p>
                  </div>
                </div>
             </div>
          </div>
        </div>

        {/* 2. TABLA DE COMPONENTES */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
           {/* HEADER TABLA + INPUTS */}
           <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10">
               <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                   <div className="flex items-center gap-2">
                       <FlaskConical size={16} className="text-[#3FF2AA]" />
                       <span className="text-[10px] font-black text-white uppercase tracking-widest">
                           Desglose de Materia Prima
                       </span>
                   </div>

                   {/* BARRA DE AGREGADO */}
                   <div className="flex items-center gap-2 w-full md:w-auto">
                        {/* Dropdown Insumos */}
                        <div className="relative flex-1 md:w-64" ref={dropdownInsumoRef} data-onboarding-id="recetas-insumo-select">
                            <div
                                onClick={() => setShowInsumoList(!showInsumoList)}
                                className="bg-white/10 border border-white/20 rounded-xl py-1.5 px-3 text-[9px] font-bold text-white flex justify-between items-center cursor-pointer hover:bg-white/20 transition-all uppercase"
                            >
                                {tempInsumo ? tempInsumo.name : "BUSCAR INSUMO..."}
                                <ChevronDown size={12} className="text-white/50" />
                            </div>
                            
                            {showInsumoList && (
                                <div className="absolute top-full right-0 mt-2 w-72 bg-white border border-slate-200 rounded-2xl shadow-xl max-h-64 overflow-y-auto animate-nova-in z-50 p-2">
                                    <input 
                                        type="text" 
                                        placeholder="BUSCAR INSUMO..." 
                                        value={busquedaInsumo} 
                                        autoFocus 
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => setBusquedaInsumo(e.target.value)} 
                                        className="w-full px-3 py-2 mb-1 bg-slate-50 border border-slate-100 rounded-xl text-[9px] font-bold uppercase outline-none" 
                                    />
                                    {insumosSeleccionables.filter(i => i.name.toLowerCase().includes(busquedaInsumo.toLowerCase())).map(i => (
                                        <div 
                                            key={i.id} 
                                            onClick={() => { setTempInsumo(i); setShowInsumoList(false); }} 
                                            className="px-3 py-2 flex justify-between items-center text-[9px] hover:bg-slate-50 rounded-lg cursor-pointer border-b border-slate-50 last:border-0 uppercase group"
                                        >
                                            <span className="font-bold text-slate-600 group-hover:text-slate-800 truncate max-w-[150px]">{i.name}</span>
                                            <span className="font-black text-slate-400 group-hover:text-[#51A7FD]">{fmtMoney(i.cost_price)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <input
                            type="number"
                            placeholder="CANT."
                            value={tempCantidad}
                            onChange={(e) => setTempCantidad(e.target.value)}
                            // FIX UX #25: Enter para agregar ingrediente
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddIngrediente();
                              }
                            }}
                            aria-label="Cantidad del insumo a agregar"
                            className="w-16 bg-white/10 border border-white/20 rounded-xl py-1.5 px-2 text-[9px] font-black text-white text-center outline-none placeholder-white/30 focus:bg-white/20"
                        />
                        
                        <button
                            data-onboarding-id="recetas-add-ingrediente"
                            onClick={handleAddIngrediente}
                            className="bg-[#3FF2AA] text-[#0B2A36] px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center gap-1 hover:scale-105 transition-all shadow-lg shadow-emerald-900/20"
                        >
                            <Plus size={12} strokeWidth={4} />
                        </button>
                   </div>
               </div>
           </div>
          
           {/* AVISO: ingredientes archivados */}
           {ingredientesArchivados.length > 0 && (
             <div className="mx-4 mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
               <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />
               <div>
                 <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                   {ingredientesArchivados.length === 1
                     ? '1 insumo archivado en esta receta'
                     : `${ingredientesArchivados.length} insumos archivados en esta receta`}
                 </p>
                 <p className="text-[10px] font-bold text-amber-600 mt-1 leading-relaxed">
                   {ingredientesArchivados.map((i) => i.nombre).join(', ')} — siguen sumando al costo,
                   pero al estar archivados no se actualizan desde Compras ni Gestión de Costos.
                   Reactivalos en Productos o reemplazalos por un insumo vigente.
                 </p>
               </div>
             </div>
           )}

           {/* TABLA LISTADO */}
           <div className="overflow-x-auto imp-table-wrap">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    <th className="px-5 py-3 pl-5">Componente</th>
                    <th className="px-4 py-3 text-right">Costo Unit. (Actual)</th>
                    <th className="px-4 py-3 text-center">Cantidad</th>
                    <th className="px-4 py-3 text-right">Subtotal</th>
                    <th className="px-5 py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {receta.ingredientes.length === 0 ? (
                    <tr>
                       <td colSpan="5" className="py-12 text-center text-slate-300">
                          <ClipboardList size={32} className="mx-auto mb-2 opacity-50" />
                          <p className="text-[9px] font-black uppercase tracking-[0.2em]">Sin componentes cargados</p>
                       </td>
                    </tr>
                  ) : (
                    receta.ingredientes.map((ing, idx) => {
                      const estaArchivado = archivedIds.has(ing.id);
                      return (
                      <tr key={idx} className={`transition-all group ${estaArchivado ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-slate-50'}`}>
                        <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${estaArchivado ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>
                                    {estaArchivado ? <AlertTriangle size={12} /> : <Package size={12} />}
                                </div>
                                <div>
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-[10px] font-black text-slate-700 uppercase">{ing.nombre}</p>
                                      {estaArchivado && (
                                        <span
                                          title="Este insumo está archivado en Productos. Su costo puede estar desactualizado."
                                          className="text-[8px] font-black uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded"
                                        >
                                          Archivado
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{ing.um}</p>
                                </div>
                            </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                           <span className="text-[10px] font-bold text-slate-500">
                              {fmtMoney(ing.costo_unitario)}
                           </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input 
                             type="number" 
                             value={ing.quantity} 
                             onChange={(e) => updateQuantity(idx, e.target.value)} 
                             className="w-16 bg-white border border-slate-200 rounded-lg py-1 px-1 text-center text-[10px] font-black text-[#51A7FD] outline-none shadow-sm focus:border-[#51A7FD] transition-all" 
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                           <span className="text-[10px] font-black text-slate-700">
                              {fmtMoney(ing.costo_unitario * ing.quantity)}
                           </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button onClick={() => removeIngrediente(idx)} className="text-slate-300 hover:text-rose-500 transition-all p-1.5 hover:bg-rose-50 rounded-lg">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
           </div>

           {/* FOOTER ACCIONES */}
           <div className="bg-slate-50 p-4 border-t border-slate-100 flex justify-between items-center">
                <div className="hidden md:flex flex-col">
                     <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Estado de Sincronización</span>
                     <span className={`text-[10px] font-black uppercase tracking-tight ${hayDesfasaje ? 'text-amber-500' : 'text-emerald-500'}`}>
                         {hayDesfasaje ? '⚠️ Costos desactualizados' : '✅ Costos al día'}
                     </span>
                </div>

                <button
                  data-onboarding-id="recetas-submit"
                  onClick={guardarReceta}
                  disabled={saving || isSuccess || receta.ingredientes.length === 0}
                  className={`px-8 py-3 rounded-2xl shadow-lg transition-all flex items-center gap-2 text-[10px] font-black uppercase tracking-widest
                    ${isSuccess 
                      ? 'bg-[#3ff2aa] text-[#0B2A36] scale-105 shadow-emerald-200' 
                      : 'bg-[#3ff2aa] text-[#0B2A36] hover:scale-[1.02] active:scale-95 shadow-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed'}`}
                >
                  {isSuccess ? (
                    <><Check size={16} strokeWidth={4} /> Guardado Exitoso</>
                  ) : saving ? (
                    <><RefreshCcw size={16} className="animate-spin" /> Procesando...</>
                  ) : (
                    <><Save size={16} /> Confirmar & Actualizar Costo</>
                  )}
                </button>
           </div>
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
};

export default Recetas;
